import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Filter, CheckCircle, XCircle, Package, Printer, AlertTriangle, Copy } from 'lucide-react';
import { MarkupCatalog, User } from '@bloom/shared';
import apiClient from '../lib/api';
import { CatalogForm } from '../components/catalogs/CatalogForm';
import { CatalogPrintView } from '../components/catalogs/CatalogPrintView';
import { SalesRepCatalogModal } from '../components/catalogs/SalesRepCatalogModal';
import { useUser } from '../store/useAuthStore';
import { formatShortDate } from '../utils/dateUtils';
import { Pagination } from '../components/common/Pagination';

interface CatalogListProps {
  onCreateNew: () => void;
  onEditCatalog: (catalog: MarkupCatalog) => void;
  onPrintCatalog: (catalog: MarkupCatalog) => void;
  onDuplicateCatalog: (catalog: MarkupCatalog) => void;
  refreshTrigger?: number;
}

const CatalogList: React.FC<CatalogListProps> = ({ onCreateNew, onEditCatalog, onPrintCatalog, onDuplicateCatalog, refreshTrigger }) => {

  const user = useUser();
  const isAdmin = user?.role === 'admin';
  const isSupervisor = user?.role === 'supervisor';

  const [catalogs, setCatalogs] = useState<MarkupCatalog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // The query actually sent: typing is debounced, and only the newest
  // request may write the list.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const loadSeq = useRef(0);
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const [showSalesRepModal, setShowSalesRepModal] = useState(false);

  const loadCatalogs = async () => {
    const mine = ++loadSeq.current;
    const searchTerm = debouncedSearch;
    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: 10,
        search: searchTerm || undefined
      };
      
      if (activeFilter !== 'all') {
        params.isActive = activeFilter === 'active';
      }

      let response;
      if (isAdmin) {
        // Admin gets all admin catalogs
        response = await apiClient.getTemplates(params);
        if (mine !== loadSeq.current) return;
      } else if (isSupervisor) {
        // Supervisors get their own catalogs (created for their AMs)
        response = await apiClient.getAssignedCatalogs();
        if (mine !== loadSeq.current) return;
        if (response.success && response.data) {
          const allCatalogs = response.data.catalogs || [];
          let filteredCatalogs = allCatalogs;
          if (searchTerm) {
            filteredCatalogs = filteredCatalogs.filter((catalog: any) =>
              catalog.catalogName.toLowerCase().includes(searchTerm.toLowerCase())
            );
          }
          if (activeFilter !== 'all') {
            filteredCatalogs = filteredCatalogs.filter((catalog: any) =>
              activeFilter === 'active' ? catalog.isActive : !catalog.isActive
            );
          }
          setPagination({
            page: currentPage,
            limit: 10,
            total: filteredCatalogs.length,
            pages: Math.ceil(filteredCatalogs.length / 10)
          });
          const start = (currentPage - 1) * 10;
          const end = start + 10;
          setCatalogs(filteredCatalogs.slice(start, end));
          setLoading(false);
          return;
        }
      } else {
        // Distributors get their assigned catalogs and their own catalogs
        response = await apiClient.getAssignedCatalogs();
        if (mine !== loadSeq.current) return;
        // /catalogs/assigned returns the complete set (no paging), so filtering
        // and paging it here is safe.
        if (response.success && response.data) {
          const allCatalogs = response.data.catalogs || [];
          // Apply filters
          let filteredCatalogs = allCatalogs;
          
          if (searchTerm) {
            filteredCatalogs = filteredCatalogs.filter((catalog: any) =>
              catalog.catalogName.toLowerCase().includes(searchTerm.toLowerCase())
            );
          }
          
          if (activeFilter !== 'all') {
            filteredCatalogs = filteredCatalogs.filter((catalog: any) =>
              activeFilter === 'active' ? catalog.isActive : !catalog.isActive
            );
          }
          
          // Set pagination manually
          setPagination({
            page: currentPage,
            limit: 10,
            total: filteredCatalogs.length,
            pages: Math.ceil(filteredCatalogs.length / 10)
          });
          
          // Apply pagination
          const start = (currentPage - 1) * 10;
          const end = start + 10;
          setCatalogs(filteredCatalogs.slice(start, end));
          setLoading(false);
          return;
        }
      }
      
      if (response.success && response.data) {
        const data = response.data as any;
        setCatalogs(data.templates || data.catalogs || []);
        setPagination(data.pagination);
      } else {
        console.error('Failed to load catalogs:', response.error);
        setCatalogs([]);
      }
    } catch (error) {
      if (mine !== loadSeq.current) return;
      console.error('Failed to load catalogs:', error);
      setCatalogs([]);
    } finally {
      if (mine === loadSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 250);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    loadCatalogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, activeFilter, refreshTrigger]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleFilterChange = (filter: 'all' | 'active' | 'inactive') => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const getAssigneeLabel = () => {
    if (isAdmin) return 'Distributors';
    if (isSupervisor) return 'Account Managers';
    return 'Physicians';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Catalog Management</h2>
          <p className="text-sm text-gray-600">
            {isAdmin
              ? 'Create and manage catalogs with fixed prices for distribution'
              : isSupervisor
              ? 'Create and manage fixed-price catalogs for your account managers'
              : 'Create and manage fixed-price catalogs for physicians'
            }
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {isAdmin && (
            <button
              onClick={() => setShowSalesRepModal(true)}
              className="inline-flex items-center justify-center px-4 py-2 border border-purple-300 rounded-md shadow-sm text-sm font-medium text-purple-700 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 w-full sm:w-auto"
            >
              Sales-rep catalog
            </button>
          )}
          <button
            onClick={onCreateNew}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Catalog
          </button>
        </div>
      </div>

      {showSalesRepModal && (
        <SalesRepCatalogModal
          catalogs={catalogs}
          onClose={() => setShowSalesRepModal(false)}
          onSaved={() => loadCatalogs()}
        />
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search catalogs..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            value={activeFilter}
            onChange={(e) => handleFilterChange(e.target.value as 'all' | 'active' | 'inactive')}
            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Catalogs</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
      </div>

      {/* Catalogs Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading catalogs...</p>
          </div>
        ) : !catalogs || catalogs.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Catalogs Available</h3>
            {(isAdmin || isSupervisor) ? (
              <p className="text-gray-600">No catalogs created yet. Create your first catalog to get started.</p>
            ) : (
              <>
                <p className="text-gray-600">No catalogs are shared with you.</p>
                <p className="text-sm text-gray-500 mt-1">Please ask your administrator for catalog access.</p>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block data-table-container">
              <div className="data-table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Catalog Name</th>
                      <th>Products</th>
                      <th>Assigned To</th>
                      <th>Tags</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(catalogs || []).map((catalog) => (
                      <tr key={catalog._id}>
                        <td className="cell-primary">
                          <div className="cell-stack">
                            <span className="cell-stack-value cell-truncate" title={catalog.catalogName}>
                              {(catalog as any).issueCount > 0 && (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 mr-2 align-middle"
                                  title={`${(catalog as any).issueCount} pricing/assignment issue${(catalog as any).issueCount === 1 ? '' : 's'} — open the catalog to review`}
                                >
                                  <AlertTriangle className="w-3 h-3" />
                                  {(catalog as any).issueCount}
                                </span>
                              )}
                              {catalog.catalogName}
                              {(catalog as any).catalogType === 'msrp' && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 ml-2">
                                  MSRP
                                </span>
                              )}
                            </span>
                            {catalog.description && (
                              <span className="cell-stack-label cell-truncate" title={catalog.description}>
                                {catalog.description}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="cell-secondary">
                          <div className="cell-stack">
                            <span className="cell-stack-value">{catalog.products?.length || 0} products</span>
                            {catalog.products && catalog.products.length > 0 && (
                              <span className="cell-stack-label cell-mono">
                                ${Math.min(...catalog.products.map((p: any) => p.price)).toFixed(2)} - ${Math.max(...catalog.products.map((p: any) => p.price)).toFixed(2)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="cell-secondary">
                          <div className="cell-stack">
                            <span className="cell-stack-value">{catalog.assignedTo?.length || 0} {getAssigneeLabel().toLowerCase()}</span>
                            {catalog.assignedTo && catalog.assignedTo.length > 0 && (
                              <span className="cell-stack-label">
                                {catalog.assignedTo.slice(0, 2).map((user: any) => user.name || user.email).join(', ')}
                                {catalog.assignedTo.length > 2 && ` +${catalog.assignedTo.length - 2} more`}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="cell-secondary">
                          {catalog.tags && catalog.tags.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {catalog.tags.slice(0, 2).map((tag: string, index: number) => (
                                <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                  {tag}
                                </span>
                              ))}
                              {catalog.tags.length > 2 && (
                                <span className="text-xs text-gray-500">+{catalog.tags.length - 2}</span>
                              )}
                            </div>
                          ) : (
                            <span className="cell-muted">—</span>
                          )}
                        </td>
                        <td className="cell-secondary">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            catalog.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {catalog.isActive ? (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Active
                              </>
                            ) : (
                              <>
                                <XCircle className="h-3 w-3 mr-1" />
                                Inactive
                              </>
                            )}
                          </span>
                        </td>
                        <td className="cell-muted">
                          {formatShortDate(catalog.createdAt)}
                        </td>
                        <td className="cell-actions">
                          <div className="table-actions">
                            <button
                              onClick={() => onPrintCatalog(catalog)}
                              className="table-action-btn table-action-btn-secondary"
                              title="Print catalog"
                            >
                              <Printer className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => onDuplicateCatalog(catalog)}
                              className="table-action-btn table-action-btn-secondary"
                              title="Duplicate catalog (copies markups, drops assignees)"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => onEditCatalog(catalog)}
                              className="table-action-btn table-action-btn-primary"
                            >
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden divide-y divide-gray-200">
              {(catalogs || []).map((catalog) => (
                <div key={catalog._id} className="p-4 bg-white hover:bg-gray-50">
                  <div className="space-y-3">
                    {/* Header with name and status */}
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-gray-900 truncate" title={catalog.catalogName}>
                          {(catalog as any).issueCount > 0 && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 mr-2 align-middle"
                              title={`${(catalog as any).issueCount} pricing/assignment issue${(catalog as any).issueCount === 1 ? '' : 's'} — open the catalog to review`}
                            >
                              <AlertTriangle className="w-3 h-3" />
                              {(catalog as any).issueCount}
                            </span>
                          )}
                          {catalog.catalogName.length > 30 ? `${catalog.catalogName.slice(0, 30)}...` : catalog.catalogName}
                          {(catalog as any).catalogType === 'msrp' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 ml-2">
                              MSRP
                            </span>
                          )}
                        </h3>
                        {catalog.description && (
                          <p className="text-sm text-gray-500 mt-1 truncate" title={catalog.description}>
                            {catalog.description.length > 30 ? `${catalog.description.slice(0, 30)}...` : catalog.description}
                          </p>
                        )}
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ml-2 ${
                        catalog.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {catalog.isActive ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Active
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3 w-3 mr-1" />
                            Inactive
                          </>
                        )}
                      </span>
                    </div>

                    {/* Main info grid */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="text-gray-500 text-xs">Products</div>
                        <div className="font-medium text-gray-900">
                          {catalog.products?.length || 0} products
                        </div>
                        {catalog.products && catalog.products.length > 0 && (
                          <div className="text-xs text-gray-500">
                            ${Math.min(...catalog.products.map((p: any) => p.price)).toFixed(2)} - ${Math.max(...catalog.products.map((p: any) => p.price)).toFixed(2)}
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <div className="text-gray-500 text-xs">Assigned To</div>
                        <div className="font-medium text-gray-900">
                          {catalog.assignedTo?.length || 0} {getAssigneeLabel().toLowerCase()}
                        </div>
                        {catalog.assignedTo && catalog.assignedTo.length > 0 && (
                          <div className="text-xs text-gray-500">
                            {catalog.assignedTo.slice(0, 1).map((user: any) => (
                              <div key={user._id}>
                                {user.name || user.email}
                              </div>
                            ))}
                            {catalog.assignedTo.length > 1 && (
                              <div>+{catalog.assignedTo.length - 1} more</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Tags */}
                    {catalog.tags && catalog.tags.length > 0 && (
                      <div>
                        <div className="text-gray-500 text-xs mb-1">Tags</div>
                        <div className="flex flex-wrap gap-1">
                          {catalog.tags.slice(0, 3).map((tag: string, index: number) => (
                            <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              {tag}
                            </span>
                          ))}
                          {catalog.tags.length > 3 && (
                            <span className="text-xs text-gray-500">+{catalog.tags.length - 3} more</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Footer with date and actions */}
                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                      <div className="text-xs text-gray-500">
                        Created {formatShortDate(catalog.createdAt)}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => onPrintCatalog(catalog)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          title="Print catalog"
                        >
                          <Printer className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onDuplicateCatalog(catalog)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          title="Duplicate catalog (copies markups, drops assignees)"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onEditCatalog(catalog)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={pagination.pages}
        onPageChange={setCurrentPage}
        totalItems={pagination.total}
        itemsPerPage={pagination.limit}
      />
    </div>
  );
};

export default function CatalogManagement() {
  const user = useUser();
  const isAdmin = user?.role === 'admin';

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCatalog, setEditingCatalog] = useState<MarkupCatalog | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAssignees, setSelectedAssignees] = useState<User[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // MSRP template picker state
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [msrpTemplates, setMsrpTemplates] = useState<any[]>([]);
  const [createInitialData, setCreateInitialData] = useState<any>(undefined);

  const handleCreateNew = async () => {
    if (isAdmin) {
      setCreateInitialData(undefined);
      setShowCreateModal(true);
      return;
    }

    // For AMs/supervisors, check for MSRP templates
    try {
      const response = await apiClient.getMsrpTemplates();
      const templates = response.success ? response.data?.templates : undefined;
      if (templates && templates.length > 0) {
        setMsrpTemplates(templates);
        setShowTemplatePicker(true);
      } else {
        setCreateInitialData(undefined);
        setShowCreateModal(true);
      }
    } catch {
      setCreateInitialData(undefined);
      setShowCreateModal(true);
    }
  };

  const handleSelectTemplate = (template: any) => {
    setShowTemplatePicker(false);
    const templateProducts = (template.products || []).map((p: any) => ({
      productId: p.productId,
      price: p.price,
      notes: p.notes
    }));
    setCreateInitialData({
      catalogName: '',
      description: '',
      products: templateProducts,
      assignedTo: [],
      tags: [],
      isActive: true
    });
    setShowCreateModal(true);
  };

  const handleStartFromScratch = () => {
    setShowTemplatePicker(false);
    setCreateInitialData(undefined);
    setShowCreateModal(true);
  };

  const handleEditCatalog = async (catalog: MarkupCatalog) => {
    setEditingCatalog(catalog);
    setShowEditModal(true);
    
    // Convert assignedTo user details to User objects for the form
    if (catalog.assignedTo && Array.isArray(catalog.assignedTo)) {
      const assignees = catalog.assignedTo.map((assigned: any) => {
        if (typeof assigned === 'object' && assigned._id) {
          // Already a user object with details
          return {
            _id: assigned._id,
            email: assigned.email,
            role: assigned.role,
            profile: {
              firstName: assigned.name?.split(' ')[0] || '',
              lastName: assigned.name?.split(' ').slice(1).join(' ') || ''
            }
          } as User;
        } else {
          // Just an ID - create a minimal user object
          return {
            _id: assigned.toString(),
            email: 'unknown@example.com',
            role: 'physician',
            password: '',
            status: 'active',
            createdAt: new Date(),
            updatedAt: new Date(),
            profile: {
              firstName: 'Unknown',
              lastName: 'User',
              phone: ''
            }
          } as User;
        }
      });
      setSelectedAssignees(assignees);
    } else {
      setSelectedAssignees([]);
    }
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setEditingCatalog(null);
    setRefreshKey(prev => prev + 1);
  };

  const handleEditClose = () => {
    setShowEditModal(false);
    setEditingCatalog(null);
    setSelectedAssignees([]);
  };

  const handleSuccess = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Print catalog state
  const [printingCatalog, setPrintingCatalog] = useState<MarkupCatalog | null>(null);

  const handlePrintCatalog = (catalog: MarkupCatalog) => {
    setPrintingCatalog(catalog);
  };

  const handleDuplicateCatalog = (catalog: MarkupCatalog) => {
    // Pre-fill the create modal with the source catalog's markups, type,
    // description, tags, and validity dates — but NOT assignees or the name.
    // The user reviews and either accepts the suggested name ("X (Copy)") or
    // tweaks anything before saving via the standard create flow.
    setSelectedAssignees([]);
    setCreateInitialData({
      catalogName: `${catalog.catalogName} (Copy)`,
      description: catalog.description || '',
      products: (catalog.products || []).map((p: any) => ({
        productId: typeof p.productId === 'string' ? p.productId : p.productId?._id || p.productId,
        price: p.price,
        notes: p.notes
      })),
      assignedTo: [],
      tags: catalog.tags ? [...catalog.tags] : [],
      isActive: true,
      catalogType: (catalog as any).catalogType || 'cost',
      validFrom: (catalog as any).validFrom,
      validUntil: (catalog as any).validUntil
    });
    setShowCreateModal(true);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <CatalogList
        onCreateNew={handleCreateNew}
        onEditCatalog={handleEditCatalog}
        onPrintCatalog={handlePrintCatalog}
        onDuplicateCatalog={handleDuplicateCatalog}
        refreshTrigger={refreshKey}
      />

      {/* Template Picker Modal */}
      {showTemplatePicker && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Create New Catalog</h3>
            <p className="text-sm text-gray-600 mb-4">
              Start from scratch or use an MSRP template as a starting point.
            </p>

            <div className="space-y-3 max-h-80 overflow-y-auto">
              <button
                type="button"
                onClick={handleStartFromScratch}
                className="w-full text-left p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="font-medium text-gray-900">Start from scratch</div>
                <div className="text-sm text-gray-500">Add products and set prices manually</div>
              </button>

              {msrpTemplates.map((template: any) => (
                <button
                  key={template._id}
                  type="button"
                  onClick={() => handleSelectTemplate(template)}
                  className="w-full text-left p-4 border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-gray-900">{template.catalogName}</div>
                      {template.description && (
                        <div className="text-sm text-gray-500 mt-1">{template.description}</div>
                      )}
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 ml-3 flex-shrink-0">
                      {template.products?.length || 0} products
                    </span>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowTemplatePicker(false)}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <CatalogForm
        mode={isAdmin ? "create-template" : "create-physician-catalog"}
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedAssignees([]);
          setCreateInitialData(undefined);
        }}
        onSuccess={() => {
          setShowCreateModal(false);
          setSelectedAssignees([]);
          setCreateInitialData(undefined);
          handleSuccess();
        }}
        initialData={createInitialData}
        enableAssignmentSelection={true}
        selectedAssignees={selectedAssignees}
        onAssigneesChange={(assignees) => setSelectedAssignees(assignees)}
      />

      {showEditModal && editingCatalog && (
        <CatalogForm
          mode={isAdmin ? "edit-template" : "edit-physician-catalog"}
          isOpen={showEditModal}
          initialData={{
            catalogName: editingCatalog.catalogName,
            description: editingCatalog.description || '',
            products: editingCatalog.products || [],
            assignedTo: (editingCatalog.assignedTo || []).map((user: any) =>
              typeof user === 'string' ? user : user._id
            ),
            tags: editingCatalog.tags || [],
            isActive: editingCatalog.isActive !== false,
            catalogType: (editingCatalog as any).catalogType || 'cost'
          }}
          catalogId={editingCatalog._id}
          enableAssignmentSelection={true}
          selectedAssignees={selectedAssignees}
          onAssigneesChange={(assignees) => setSelectedAssignees(assignees)}
          onClose={handleEditClose}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Print View Modal */}
      {printingCatalog && (
        <CatalogPrintView
          catalog={printingCatalog}
          onClose={() => setPrintingCatalog(null)}
        />
      )}
    </div>
  );
}
